import React, { useState, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Toast from '../../components/UI/Toast';
import { useDebounce } from '../../hooks/useDebounce';
import {
    UserGroupIcon,
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowPathIcon,
    EnvelopeIcon,
    PhoneIcon,
    BriefcaseIcon,
    AcademicCapIcon
} from '@heroicons/react/24/outline';

interface TeamMember {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    qualification: string;
    experience: string;
    company: string;
    skillsDescription: string;
    isActive: boolean;
    isVerified: boolean;
    createdAt: string;
    lastLogin?: string;
    earnings: {
        totalEarnings: number;
        pendingAmount: number;
        paidAmount: number;
        referralEarnings: number;
        validatedEarnings: number;
        withdrawnAmount: number;
    };
}

const CompanyTeam: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const fetchTeamMembers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const companyId = (user as any)?.id || (user as any)?._id;
            if (!companyId) {
                setError('Company ID not found');
                return;
            }

            const response = await api.get(`/companies/${companyId}/users`);
            setTeamMembers(response.data.data || []);
            setError(null);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to fetch team members';
            setError(errorMessage);
            dispatch(showToast({ message: errorMessage, type: 'error' }));
            console.error('Error fetching team members:', err);
        } finally {
            setLoading(false);
        }
    }, [user, dispatch]);

    const refreshTeamMembers = async () => {
        setRefreshing(true);
        await fetchTeamMembers();
        setRefreshing(false);
    };

    const handleToggleMemberStatus = async (memberId: string, currentStatus: boolean) => {
        try {
            const newStatus = !currentStatus;
            await api.put(`/users/${memberId}`, { isActive: newStatus });

            setTeamMembers(teamMembers.map(member =>
                member._id === memberId ? { ...member, isActive: newStatus } : member
            ));

            dispatch(showToast({
                message: `Member ${newStatus ? 'activated' : 'deactivated'} successfully`,
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update member status',
                type: 'error'
            }));
        }
    };

    const handleDeleteMember = async (memberId: string) => {
        if (!window.confirm('Are you sure you want to remove this team member?')) return;

        try {
            await api.delete(`/users/${memberId}`);
            setTeamMembers(teamMembers.filter(member => member._id !== memberId));
            dispatch(showToast({
                message: 'Team member removed successfully',
                type: 'success'
            }));
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to remove team member',
                type: 'error'
            }));
        }
    };


    useEffect(() => {
        fetchTeamMembers();
    }, [fetchTeamMembers]);

    const filteredTeamMembers = teamMembers.filter(member => {
        const matchesSearch =
            member.firstName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            member.lastName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            member.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            (member.skillsDescription && member.skillsDescription.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && member.isActive) ||
            (statusFilter === 'inactive' && !member.isActive) ||
            (statusFilter === 'verified' && member.isVerified) ||
            (statusFilter === 'unverified' && !member.isVerified);

        return matchesSearch && matchesStatus;
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatLastLogin = (lastLogin?: string) => {
        if (!lastLogin) return 'Never';
        const date = new Date(lastLogin);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 24) return `${diffInHours}h ago`;
        return formatDate(lastLogin);
    };

    const getStatusColor = (member: TeamMember) => {
        if (!member.isActive) return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
        if (member.isVerified) return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    };

    const getStatusText = (member: TeamMember) => {
        if (!member.isActive) return 'Inactive';
        if (member.isVerified) return 'Verified';
        return 'Pending';
    };

    const getExperienceColor = (experience: string) => {
        switch (experience.toLowerCase()) {
            case 'senior':
                return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
            case 'mid-level':
                return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
            case 'junior':
                return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
            default:
                return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
        }
    };

    const stats = {
        totalMembers: teamMembers.length,
        activeMembers: teamMembers.filter(m => m.isActive).length,
        verifiedMembers: teamMembers.filter(m => m.isVerified).length,
        pendingMembers: teamMembers.filter(m => !m.isVerified && m.isActive).length
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold">Team Management</h1>
                        <p className="text-blue-100 mt-1">
                            Manage your company team members and their roles
                        </p>
                    </div>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <UserGroupIcon className="h-8 w-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Members</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalMembers}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Members</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeMembers}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <CheckCircleIcon className="h-8 w-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Verified</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.verifiedMembers}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center">
                        <XCircleIcon className="h-8 w-8 text-yellow-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingMembers}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="flex flex-col md:flex-row gap-4 flex-1">
                        {/* Search */}
                        <div className="relative flex-1 max-w-md">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search team members..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="h-5 w-5 text-gray-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="verified">Verified</option>
                                <option value="unverified">Unverified</option>
                            </select>
                        </div>

                        {/* Refresh Button */}
                        <button
                            onClick={refreshTeamMembers}
                            disabled={refreshing}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                        >
                            <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                    <div className="flex">
                        <XCircleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Team Members Grid */}
            {filteredTeamMembers.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No team members found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6">
                        {searchTerm || statusFilter !== 'all'
                            ? 'Try adjusting your search or filter criteria'
                            : 'No team members found'
                        }
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTeamMembers.map((member) => (
                        <div key={member._id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-semibold">
                                        {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {member.firstName} {member.lastName}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                                    </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(member)}`}>
                                    {getStatusText(member)}
                                </span>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <PhoneIcon className="h-4 w-4 mr-2" />
                                    {member.phone}
                                </div>

                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <BriefcaseIcon className="h-4 w-4 mr-2" />
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getExperienceColor(member.experience)}`}>
                                        {member.experience}
                                    </span>
                                </div>

                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                    <AcademicCapIcon className="h-4 w-4 mr-2" />
                                    {member.qualification}
                                </div>

                                {member.skillsDescription && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        <strong>Skills:</strong> {member.skillsDescription}
                                    </div>
                                )}

                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 dark:text-gray-400">
                                        Joined: {formatDate(member.createdAt)}
                                    </span>
                                    <span className="text-gray-500 dark:text-gray-400">
                                        Last active: {formatLastLogin(member.lastLogin)}
                                    </span>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400">Total Earnings</p>
                                            <p className="font-semibold text-green-600 dark:text-green-400">
                                                ₹{member.earnings.totalEarnings.toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400">Pending</p>
                                            <p className="font-semibold text-yellow-600 dark:text-yellow-400">
                                                ₹{member.earnings.pendingAmount.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => {
                                        setSelectedMember(member);
                                        setShowDetailsModal(true);
                                    }}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                    View Details
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleToggleMemberStatus(member._id, member.isActive)}
                                        className={member.isActive
                                            ? "text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 transition-colors"
                                            : "text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                                        }
                                        title={member.isActive ? 'Deactivate Member' : 'Activate Member'}
                                    >
                                        {member.isActive ? <XCircleIcon className="h-4 w-4" /> : <CheckCircleIcon className="h-4 w-4" />}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteMember(member._id)}
                                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                        title="Remove Member"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Member Details Modal */}
            {selectedMember && showDetailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Member Details
                                </h2>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Profile Section */}
                                <div className="flex items-center space-x-4">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full w-16 h-16 flex items-center justify-center font-semibold text-xl">
                                        {selectedMember.firstName.charAt(0)}{selectedMember.lastName.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                            {selectedMember.firstName} {selectedMember.lastName}
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">{selectedMember.email}</p>
                                        <span className={`px-3 py-1 text-sm font-medium rounded-full mt-2 inline-block ${getStatusColor(selectedMember)}`}>
                                            {getStatusText(selectedMember)}
                                        </span>
                                    </div>
                                </div>

                                {/* Contact Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center gap-3">
                                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{selectedMember.phone}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{selectedMember.email}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Professional Information */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Experience Level</p>
                                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getExperienceColor(selectedMember.experience)}`}>
                                            {selectedMember.experience}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Qualification</p>
                                        <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedMember.qualification}</p>
                                    </div>
                                </div>

                                {/* Skills */}
                                {selectedMember.skillsDescription && (
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Skills</p>
                                        <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                            {selectedMember.skillsDescription}
                                        </p>
                                    </div>
                                )}

                                {/* Earnings */}
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">

                                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Earnings Summary</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
                                            <p className="font-bold text-green-600 dark:text-green-400">
                                                ₹{selectedMember.earnings.totalEarnings.toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                                            <p className="font-bold text-yellow-600 dark:text-yellow-400">
                                                ₹{selectedMember.earnings.pendingAmount.toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Paid</p>
                                            <p className="font-bold text-blue-600 dark:text-blue-400">
                                                ₹{selectedMember.earnings.paidAmount.toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Referrals</p>
                                            <p className="font-bold text-purple-600 dark:text-purple-400">
                                                ₹{selectedMember.earnings.referralEarnings.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Joined Company</p>
                                        <p className="font-medium text-gray-900 dark:text-white">{formatDate(selectedMember.createdAt)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Last Login</p>
                                        <p className="font-medium text-gray-900 dark:text-white">{formatLastLogin(selectedMember.lastLogin)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            <Toast />
        </div>
    );
};

export default CompanyTeam;
